import { useEffect, useRef, useState } from 'react';

/**
 * useInViewport — Phase 49 · Feed performance.
 * -----------------------------------------------------------------
 * Lightweight IntersectionObserver hook used to defer expensive
 * media rendering (videos, high-res images) until the container has
 * been (or is close to being) scrolled into view.
 *
 * Two flags are exposed:
 *   • `inView`: boolean — currently intersecting.
 *   • `hasEnteredOnce`: boolean — has EVER been in view. Latches on
 *     so we don't unmount the media the moment it scrolls off; that
 *     would cause a costly re-download on every scroll direction
 *     change.
 *
 * Options:
 *   • rootMargin: pre-warm distance. Default 400px so the media has
 *     started loading before it's actually visible → smoother scroll.
 *   • threshold: IO threshold. Default 0.01.
 *   • once: if true, disconnects the observer after the first hit.
 */
export function useInViewport({ rootMargin = '400px', threshold = 0.01, once = false } = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const [hasEnteredOnce, setHasEnteredOnce] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      // Fallback: assume visible so behaviour degrades to eager loading.
      setInView(true);
      setHasEnteredOnce(true);
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        const visible = entry.isIntersecting;
        setInView(visible);
        if (visible) {
          setHasEnteredOnce(true);
          if (once) obs.disconnect();
        }
      }
    }, { rootMargin, threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [rootMargin, threshold, once]);

  return { ref, inView, hasEnteredOnce };
}
